//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Property cross reference list tab
//
//  Copyright:    © 2021 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//region Version 1.0.0.0
//    001            Sean Flook                  Initial Revision.
//    002   06.10.23 Sean Flook                  Use colour variables.
//    003   27.10.23 Sean Flook                  Use new dataFormStyle.
//    004   24.11.23 Sean Flook                  Moved Box and Stack to @mui/system and fixed a warning.
//    005   08.12.23 Sean Flook                  Migrated DataGrid to v6.
//    006   05.01.24 Sean Flook                  Changes to sort out warnings and use CSS shortcuts.
//    007   25.01.24 Sean Flook                  Changes required after UX review.
//    008   16.02.24 Sean Flook         ESU16_GP If changing page etc ensure the information and selection controls are cleared.
//    009   20.02.24 Sean Flook         ESU16_GP Undone above change as not required.
//    010   11.03.24 Sean Flook            GLB12 Adjusted height to remove gap.
//    011   18.03.24 Sean Flook            GLB12 Adjusted height to remove overflow.
//    012   18.03.24 Sean Flook       STRFRM3_OS Set the styling for the header row of the data grid.
//    013   22.03.24 Sean Flook            GLB12 Changed to use dataFormStyle so height can be correctly set.
//    014   26.03.24 Joshua McCormick  IMANN-336 Added xrefKey field name to displayActionButtons to give same display properties, added flex25 to historic
//    015   27.03.24 Sean Flook        IMANN-336 Undone above changes as already done under STRFRM3_OS.
//    016   29.04.24 Joshua McCormick  IMANN-386 Toolbar changes no title no wrapping with width restrictions
//    017   20.06.24 Sean Flook        IMANN-636 Use the new user rights.
//endregion Version 1.0.0.0
//region Version 1.0.1.0
//    018   20.06.24 Sean Flook       IMANN-1003 Removed the add icon when we have no records.
//    019   31.10.24 Sean Flook       IMANN-1012 Changed height of skeleton control.
//endregion Version 1.0.1.0
//region Version 1.0.5.0
//    020   27.01.25 Sean Flook       IMANN-1077 Upgraded MUI to v6.
//    021   14.03.25 Sean Flook        IMANN-963 Prevent the selection control from displaying if the user cannot edit the data.
//    022   23.04.25 Sean Flook       IMANN-1768 Changes required for selecting properties in property and street forms.
//    023   24.04.25 Sean Flook       IMANN-1760 Prevent multiple renders from occurring.
//endregion Version 1.0.5.0
//region Version 1.0.6.0
//    024   04.07.25 Sean Flook                  Set the new variant parameter for the ADSSelectionControl.
//    025   04.08.25 Sean Flook       IMANN-1960 Replace Historic with End Date.
//    026   08.08.25 Sean Flook       IMANN-1960 Swap order of styles.
//    027   08.08.25 Sean Flook       IMANN-1960 Moved styling for DataGrid column headers to App.css.
//endregion Version 1.0.6.0
//region Version 1.0.7.0
//    028   13.06.25 Sean Flook       IMANN-1812 Moved controls from @mui/system to @mui/material.
//    029   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI to v7.
//    030   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI X to v8.
//    031   27.10.25 Sean Flook       IMANN-2110 Set getRowHeight to Auto in the grid so that text wraps correctly.
//    032   28.10.25 Sean Flook       IMANN-2110 Use formatTableEndDate for date column and added renderCell for the cross reference column.
//    033   17.12.25 Sean Flook       IMANN-2196 Removed the information and selection components as no longer required in this control.
//endregion Version 1.0.7.0
//region Version 1.0.7.2
//    034   01.05.26 Sean Flook       IMANN-2430 Use currentCrossReferenceSelection when using the list of ids currently selected when deleting.
//endregion Version 1.0.7.2
//
//--------------------------------------------------------------------------------------------------
//endregion header

import React, { useContext, useState, useRef, useEffect, Fragment } from "react";
import PropTypes from "prop-types";

import UserContext from "../context/userContext";
import LookupContext from "../context/lookupContext";
import SettingsContext from "../context/settingsContext";
import SelectionContext from "../context/selectionContext";

import { copyTextToClipboard, formatTableEndDate, GetCrossRefAvatar } from "../utils/HelperUtils";
import { getBilingualSource } from "../utils/PropertyUtils";
import {
  Tooltip,
  IconButton,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Box,
  Stack,
} from "@mui/material";

import ADSActionButton from "../components/ADSActionButton";

import ConfirmDeleteDialog from "../dialogs/ConfirmDeleteDialog";

import { AddCircleOutlineOutlined as AddCircleIcon } from "@mui/icons-material";

import { adsBlueA, adsLightBlue10 } from "../utils/ADSColours";
import { toolbarStyle, ActionIconStyle, dataFormStyle, tooltipStyle, StyledDataGrid } from "../utils/ADSStyles";
import { useTheme } from "@mui/material/styles";

PropertyCrossRefListTab.propTypes = {
  data: PropTypes.array,
  errors: PropTypes.array,
  loading: PropTypes.bool.isRequired,
  onSetCopyOpen: PropTypes.func.isRequired,
  onCrossRefSelected: PropTypes.func.isRequired,
  onCrossRefDelete: PropTypes.func.isRequired,
  onMultiCrossRefDelete: PropTypes.func.isRequired,
};

function PropertyCrossRefListTab({
  data,
  errors,
  loading,
  onSetCopyOpen,
  onCrossRefSelected,
  onCrossRefDelete,
  onMultiCrossRefDelete,
}) {
  const theme = useTheme();

  const userContext = useContext(UserContext);
  const lookupContext = useContext(LookupContext);
  const settingsContext = useContext(SettingsContext);
  const selectionContext = useContext(SelectionContext);

  const [sortModel, setSortModel] = useState([{ field: "source", sort: "asc" }]);
  const [selectionModel, setSelectionModel] = useState({
    type: "include",
    ids: new Set([]),
  });
  const [selectedRow, setSelectedRow] = useState(null);

  const [userCanEdit, setUserCanEdit] = useState(false);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  const deleteCrossRefId = useRef(null);
  const [associatedRecords, setAssociatedRecords] = useState(null);

  /**
   * Event to handle when the mouse enters a row in the data grid.
   *
   * @param {object} event The event object.
   */
  const handleRowMouseEnter = (event) => {
    event.preventDefault();
    setSelectedRow(Number(event.currentTarget.getAttribute("data-id")));
  };

  /**
   * Event to handle when the mouse leaves a row in the data grid.
   */
  const handleRowMouseLeave = () => {
    setSelectedRow(null);
  };

  /**
   * Method to display the action buttons for the given row.
   *
   * @param {object} params The grid row parameters.
   * @returns {JSX.Element} The action buttons for the row
   */
  const displayActionButtons = (params) => {
    const onCopyClick = (event) => {
      event.stopPropagation();
      handleCopyCrossReference(params.row.crossReference);
    };

    const onDeleteClick = (event) => {
      event.stopPropagation();
      deleteCrossRefId.current = params.id;
      if (params.row.sourceId === getBilingualSource(lookupContext)) {
        setAssociatedRecords([{ type: "linked lpi", count: 2 }]);
      } else setAssociatedRecords(null);
      setOpenDeleteConfirmation(true);
    };

    return (
      selectedRow &&
      params.id === selectedRow &&
      selectionModel &&
      selectionModel.ids.size < 2 && (
        <Stack direction="row" spacing={1} justifyContent="flex-start" alignItems="center">
          <ADSActionButton
            id={`copy-cross-ref-${params.id}`}
            variant="copy"
            inheritBackground
            tooltipTitle="Copy cross reference"
            tooltipPlacement="bottom"
            onClick={onCopyClick}
          />
          <ADSActionButton
            id={`delete-cross-ref-${params.id}`}
            variant="delete"
            inheritBackground
            disabled={!userCanEdit}
            tooltipTitle="Delete cross reference"
            tooltipPlacement="bottom"
            onClick={onDeleteClick}
          />
        </Stack>
      )
    );
  };

  /**
   * Array of fields (columns) to be displayed in the data grid.
   */
  const columns = [
    { field: "id" },
    { field: "changeType" },
    { field: "uprn" },
    { field: "xrefKey" },
    { field: "source" },
    {
      field: "sourceId",
      headerName: "Source",
      display: "flex",
      flex: 15,
      renderCell: GetCrossRefAvatar,
    },
    {
      field: "crossReference",
      headerName: "Cross reference",
      flex: 25,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ whiteSpace: "normal", wordWrap: "break-word" }}>
          {params.value}
        </Typography>
      ),
    },
    { field: "entryDate" },
    { field: "startDate" },
    {
      field: "endDate",
      headerName: "End date",
      display: "flex",
      flex: 15,
      sortable: false,
      type: "date",
      align: "right",
      headerAlign: "right",
      valueGetter: (value, row, column, apiRef) => new Date(value),
      renderCell: formatTableEndDate,
    },
    { field: "lastUpdateDate" },
    { field: "neverExport" },
    {
      field: "",
      display: "flex",
      sortable: false,
      renderCell: displayActionButtons,
    },
  ];

  /**
   * Event to handle when a cross reference record is clicked.
   *
   * @param {object} param The grid row parameters.
   * @param {object} event The event object.
   */
  const handleCrossRefClicked = (param, event) => {
    event.stopPropagation();
    if (param && param.field !== "__check__" && param.field !== "") {
      let record = -1;
      for (let i = 0; i < data.length; i++) {
        if (data[i].id === param.id) {
          record = i;
          break;
        }
      }
      if (onCrossRefSelected) onCrossRefSelected(param.id, param.row, record, data.length);
    }
  };

  /**
   * Event to handle when the selection model for the data grid changes.
   *
   * @param {object} selectionModel The current selection model for the data grid.
   */
  const handleSelectionModelChange = (selectionModel) => {
    if (selectionModel.type === "include" && selectionModel.ids.size > 0 && userCanEdit) {
      selectionContext.onSelectionVariantChange("crossReference");
      selectionContext.onSelectionCountChange(selectionModel.ids.size);
      selectionContext.onCurrentCrossReferenceChange([...selectionModel.ids]);
    } else if (selectionModel.type === "exclude" && selectionModel.ids.size === 0 && userCanEdit) {
      selectionContext.onSelectionVariantChange("crossReference");
      selectionContext.onSelectionCountChange(data.length);
      const allIds = data.map((x) => x.id);
      selectionContext.onCurrentCrossReferenceChange(allIds);
    } else if (selectionModel.type === "exclude" && selectionModel.ids.size < data.length && userCanEdit) {
      selectionContext.onSelectionVariantChange("crossReference");
      selectionContext.onSelectionCountChange(data.length - selectionModel.ids.size);
      const allIds = data.filter((x) => !selectionModel.ids.has(x.id)).map((x) => x.id);
      selectionContext.onCurrentCrossReferenceChange(allIds);
    } else {
      selectionContext.onCloseSelection();
    }
  };

  /**
   * Event to handle when the add cross reference button is clicked.
   *
   * @param {object} event The event object.
   */
  const handleAddCrossRefClick = (event) => {
    event.stopPropagation();
    if (onCrossRefSelected) onCrossRefSelected(0, null, null, null);
  };

  /**
   * Method used to copy the text to the clipboard.
   *
   * @param {string|null} text The text that needs to be copied to the clipboard.
   * @param {string} dataType The type of data that is being copied to the clipboard.
   */
  const itemCopy = (text, dataType) => {
    if (text) {
      copyTextToClipboard(text);
      if (onSetCopyOpen) onSetCopyOpen(true, dataType);
    }
  };

  /**
   * Event to copy the given cross reference to the clipboard.
   *
   * @param {string} crossRef The cross reference that needs to be copied to the clipboard.
   */
  const handleCopyCrossReference = (crossRef) => {
    if (crossRef) itemCopy(crossRef, "Cross reference");
  };

  /**
   * Event to handle when the delete confirmation dialog is closed.
   *
   * @param {boolean} deleteConfirmed True if the user has confirmed the delete; otherwise false.
   */
  const handleCloseDeleteConfirmation = (deleteConfirmed) => {
    setOpenDeleteConfirmation(false);
    if (deleteConfirmed) {
      if (selectionContext.currentCrossReferenceSelection?.length > 0 && onMultiCrossRefDelete) {
        onMultiCrossRefDelete(selectionContext.currentCrossReferenceSelection);
        selectionContext.onCloseSelection();
        setSelectionModel({
          type: "include",
          ids: new Set([]),
        });
      } else if (deleteCrossRefId.current && onCrossRefDelete) {
        onCrossRefDelete(deleteCrossRefId.current);
        deleteCrossRefId.current = null;
      }
    }
  };

  /**
   * Method to determine if the row is valid or not.
   *
   * @param {number} id The id of the row.
   * @returns {boolean} True if the row is valid; otherwise false.
   */
  const isRowInvalid = (id) => {
    if (errors && errors.length > 0) {
      const rowErrors = errors.filter((x) => x.id === id);
      return rowErrors && rowErrors.length > 0;
    } else return false;
  };

  useEffect(() => {
    if (userCanEdit !== (userContext.currentUser && userContext.currentUser.editProperty))
      setUserCanEdit(userContext.currentUser && userContext.currentUser.editProperty);
  }, [userCanEdit, userContext.currentUser]);

  useEffect(() => {
    if (selectionContext.closeControl) {
      selectionContext.onControlClosed();
      setSelectionModel({
        type: "include",
        ids: new Set([]),
      });
    }
  }, [selectionContext, selectionContext.closeControl]);

  useEffect(() => {
    if (selectionContext.deleteCrossReference && selectionContext.currentCrossReferenceSelection?.length > 0) {
      selectionContext.onDeleteCrossReference(false);
      if (settingsContext.isWelsh) {
        const bilingualSource = getBilingualSource(lookupContext);

        const selectedCrossRefs = data.filter(
          (x) => selectionContext.currentCrossReferenceSelection.includes(x.id) && x.sourceId === bilingualSource
        );

        if (selectedCrossRefs && selectedCrossRefs.length > 0) {
          setAssociatedRecords([{ type: "linked lpi", count: selectedCrossRefs.length * 2 }]);
        } else setAssociatedRecords(null);
      }
      setOpenDeleteConfirmation(true);
    }
  }, [data, lookupContext, selectionContext, selectionModel, settingsContext.isWelsh]);

  return (
    <Fragment>
      <Box sx={toolbarStyle} id="ads-property-cross-ref-list-tab">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1" noWrap sx={{ pl: theme.spacing(2) }}>
            Cross references
          </Typography>
          <Tooltip title="Add new cross reference record" arrow placement="right" sx={tooltipStyle}>
            <IconButton sx={ActionIconStyle()} disabled={!userCanEdit} onClick={handleAddCrossRefClick} size="small">
              <AddCircleIcon />
              <Typography
                variant="subtitle1"
                noWrap
                sx={{
                  pl: theme.spacing(1),
                  pr: theme.spacing(1),
                }}
              >
                Cross reference
              </Typography>
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
      <Box
        sx={{
          ...dataFormStyle("PropertyCrossRefListTab"),
        }}
      >
        {loading ? (
          <Skeleton variant="rectangular" height="30px" width="100%" />
        ) : data && data.length > 0 ? (
          <StyledDataGrid
            rows={data}
            columns={columns}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  id: false,
                  changeType: false,
                  uprn: false,
                  xrefKey: false,
                  source: false,
                  entryDate: false,
                  startDate: false,
                  lastUpdateDate: false,
                  neverExport: false,
                },
              },
            }}
            autoPageSize
            checkboxSelection
            disableColumnMenu
            disableRowSelectionOnClick
            pagination
            sortModel={sortModel}
            rowSelectionModel={selectionModel}
            slotProps={{
              row: {
                onMouseEnter: handleRowMouseEnter,
                onMouseLeave: handleRowMouseLeave,
              },
            }}
            getRowClassName={(params) => `${isRowInvalid(params.id) ? "invalid-row" : "valid-row"}`}
            getRowHeight={(params) => "auto"}
            onCellClick={handleCrossRefClicked}
            onRowSelectionModelChange={(newSelectionModel) => {
              setSelectionModel(newSelectionModel);
              handleSelectionModelChange(newSelectionModel);
            }}
            onSortModelChange={(model) => setSortModel(model)}
          />
        ) : (
          <List
            sx={{
              width: "100%",
              backgroundColor: theme.palette.background.paper,
              pt: theme.spacing(0),
            }}
            component="nav"
            key="key_no_records"
          >
            <ListItemButton
              dense
              disabled={!userCanEdit}
              disableGutters
              onClick={handleAddCrossRefClick}
              sx={{
                height: "30px",
                "&:hover": {
                  backgroundColor: adsLightBlue10,
                  color: adsBlueA,
                },
              }}
            >
              <ListItemText primary={<Typography variant="subtitle1">No cross reference records present</Typography>} />
            </ListItemButton>
          </List>
        )}
      </Box>
      <div>
        <ConfirmDeleteDialog
          variant="app cross ref"
          recordCount={selectionModel && selectionModel.ids.size > 0 ? selectionModel.ids.size : 0}
          open={openDeleteConfirmation}
          associatedRecords={associatedRecords}
          onClose={handleCloseDeleteConfirmation}
        />
      </div>
    </Fragment>
  );
}

export default PropertyCrossRefListTab;
